import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API, withCredentials: true });

http.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && !url.includes("/auth/") && typeof window !== "undefined") {
      const path = window.location.pathname;
      if (path.startsWith("/app")) window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  me: () => http.get("/auth/me").then((r) => r.data),
  login: (email, password) => http.post("/auth/login", { email, password }).then((r) => r.data),
  register: (email, password, name) => http.post("/auth/register", { email, password, name }).then((r) => r.data),
  logout: () => http.post("/auth/logout").then((r) => r.data),
  forgot: (email) => http.post("/auth/forgot-password", { email }).then((r) => r.data),
  reset: (token, password) => http.post("/auth/reset-password", { token, password }).then((r) => r.data),
  googleSession: (session_id) => http.post("/auth/google/session", { session_id }).then((r) => r.data),
};

export const dataApi = {
  listAppointments: () => http.get("/appointments").then((r) => r.data),
  createAppointment: (d) => http.post("/appointments", d).then((r) => r.data),
  updateAppointment: (id, d) => http.put(`/appointments/${id}`, d).then((r) => r.data),
  deleteAppointment: (id) => http.delete(`/appointments/${id}`).then((r) => r.data),
  listTherapists: () => http.get("/therapists").then((r) => r.data),
  createTherapist: (d) => http.post("/therapists", d).then((r) => r.data),
  updateTherapist: (id, d) => http.put(`/therapists/${id}`, d).then((r) => r.data),
  deleteTherapist: (id) => http.delete(`/therapists/${id}`).then((r) => r.data),
  getAvailability: (tid) => http.get(`/availability/${tid}`).then((r) => r.data),
  setAvailability: (tid, week) => http.put(`/availability/${tid}`, { week }).then((r) => r.data),
  listBlocks: (tid) => http.get(`/blocks/${tid}`).then((r) => r.data),
  createBlock: (tid, d) => http.post(`/blocks/${tid}`, d).then((r) => r.data),
  deleteBlock: (id) => http.delete(`/blocks/${id}`).then((r) => r.data),
  listClients: () => http.get("/clients").then((r) => r.data),
  createClient: (d) => http.post("/clients", d).then((r) => r.data),
  updateClient: (id, d) => http.put(`/clients/${id}`, d).then((r) => r.data),
  deleteClient: (id) => http.delete(`/clients/${id}`).then((r) => r.data),
  listEvents: () => http.get("/events").then((r) => r.data),
  createEvent: (d) => http.post("/events", d).then((r) => r.data),
  updateEvent: (id, d) => http.put(`/events/${id}`, d).then((r) => r.data),
  deleteEvent: (id) => http.delete(`/events/${id}`).then((r) => r.data),
};

export const affiliateApi = {
  listProducts: () => http.get("/products").then((r) => r.data),
  createProduct: (d) => http.post("/products", d).then((r) => r.data),
  updateProduct: (id, d) => http.put(`/products/${id}`, d).then((r) => r.data),
  deleteProduct: (id) => http.delete(`/products/${id}`).then((r) => r.data),
  listAffiliates: () => http.get("/affiliates").then((r) => r.data),
  createAffiliate: (d) => http.post("/affiliates", d).then((r) => r.data),
  updateAffiliate: (id, d) => http.put(`/affiliates/${id}`, d).then((r) => r.data),
  deleteAffiliate: (id) => http.delete(`/affiliates/${id}`).then((r) => r.data),
  listSales: () => http.get("/affiliate-sales").then((r) => r.data),
  createSale: (d) => http.post("/affiliate-sales", d).then((r) => r.data),
  updateSale: (id, d) => http.put(`/affiliate-sales/${id}`, d).then((r) => r.data),
  deleteSale: (id) => http.delete(`/affiliate-sales/${id}`).then((r) => r.data),
  listCommissions: () => http.get("/commissions").then((r) => r.data),
  updateCommission: (id, d) => http.put(`/commissions/${id}`, d).then((r) => r.data),
  payAllCommissions: (affiliateId) => http.post(`/affiliates/${affiliateId}/pay-all`).then((r) => r.data),
  listReferrals: () => http.get("/referrals").then((r) => r.data),
  trackReferral: (code) => http.post("/public/referrals", { code }).then((r) => r.data),
};

export const publicApi = {
  getAppointment: (id, t = "") => http.get(`/public/appointments/${id}${t ? `?t=${encodeURIComponent(t)}` : ""}`).then((r) => r.data),
  confirm: (id, t = "") => http.post(`/public/appointments/${id}/confirm${t ? `?t=${encodeURIComponent(t)}` : ""}`).then((r) => r.data),
  affiliatePortal: (code) => http.get(`/public/affiliate/${code}`).then((r) => r.data),
};

export const reminderApi = {
  phrase: (kind = "recordatorio") =>
    http.post(`/reminder-phrase?kind=${encodeURIComponent(kind)}`).then((r) => r.data.phrase),
};

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const DIAS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];

export const formatFecha = (iso) => {
  const d = new Date(iso);
  return `${d.getDate()} de ${MESES[d.getMonth()]}`;
};
export const formatDiaFecha = (iso) => {
  const d = new Date(iso);
  return `${DIAS[d.getDay()]} ${d.getDate()} ${MESES[d.getMonth()].slice(0, 3)}`;
};
export const formatHora = (iso) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
export const formatPrecio = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

const MESES_LARGOS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
export const formatPeriodo = (p) => {
  if (!p) return "";
  const [y, m] = p.split("-");
  return `${MESES_LARGOS[Number(m) - 1]} ${y}`;
};

export const buildCommissionPaidMessage = (affiliate, commission, product) => {
  const saludo = affiliate && affiliate.name ? `Hola ${affiliate.name},` : "Hola,";
  const periodo = formatPeriodo(commission.period);
  const prod = product && product.name ? ` (${product.name})` : "";
  return `${saludo} te confirmo el pago de tu comisión de ${formatPrecio(commission.amount)} de ${periodo}${prod}. Gracias por tu trabajo.`;
};

export const buildBulkPaidMessage = (affiliate, total, count) => {
  const saludo = affiliate && affiliate.name ? `Hola ${affiliate.name},` : "Hola,";
  const n = count === 1 ? "1 comisión" : `${count} comisiones`;
  return `${saludo} te confirmo el pago de ${n} por un total de ${formatPrecio(total)}. Gracias por tu trabajo.`;
};
export const isSameDay = (iso, ref) => {
  const a = new Date(iso);
  return a.getDate() === ref.getDate() && a.getMonth() === ref.getMonth() && a.getFullYear() === ref.getFullYear();
};
export const isPast = (iso) => new Date(iso).getTime() < Date.now();
export const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};
export const confirmLink = (appt) =>
  `${typeof window !== "undefined" ? window.location.origin : ""}/confirmar/${appt.id}${appt.confirm_token ? `?t=${appt.confirm_token}` : ""}`;

export const whatsappMessage = (appt) =>
  `Hola ${appt.client_name}, te recuerdo tu cita del ${formatFecha(appt.appointment_datetime)} a las ${formatHora(appt.appointment_datetime)}. Para confirmar, responde SÍ o pulsa aquí: ${confirmLink(appt)}`;
export const whatsappLink = (appt) => {
  const phone = (appt.client_phone || "").replace(/[^0-9]/g, "");
  return `https://wa.me/${phone}?text=${encodeURIComponent(whatsappMessage(appt))}`;
};

export const waLink = (phone, text) =>
  `https://wa.me/${(phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(text)}`;

export const buildReminderMessage = (appt, phrase) => {
  const saludo = appt.client_name ? `Hola ${appt.client_name},` : "Hola,";
  const fecha = formatFecha(appt.appointment_datetime);
  const hora = formatHora(appt.appointment_datetime);
  return `${saludo} te recuerdo que mañana tienes tu cita el ${fecha} a las ${hora}.\nQuería enviarte esta pequeña nota para que lo tengas presente y puedas organizarte con tranquilidad.\n${phrase}\nNos vemos pronto.`;
};

export const buildBirthdayMessage = (client, phrase) => {
  const saludo = client.name ? `Hola ${client.name},` : "Hola,";
  return `${saludo} ${phrase}\nUn abrazo.`;
};

export const buildChristmasMessage = (client, phrase) => {
  const saludo = client.name ? `Hola ${client.name},` : "Hola,";
  return `${saludo} ${phrase}\nUn abrazo.`;
};

export const buildEventMessage = (event, client) => {
  const saludo = client && client.name ? `Hola ${client.name},` : "Hola,";
  const tipo = event.kind === "conferencia" ? "una conferencia" : "un curso";
  const cuando = event.date
    ? ` el ${formatFecha(event.date)}${event.time ? ` a las ${event.time}` : ""}`
    : "";
  const donde = event.location ? ` en ${event.location}` : "";
  const extra = event.description ? `\n${event.description}` : "";
  return `${saludo} quería avisarte de ${tipo}: ${event.title}${cuando}${donde}.${extra}\nSi te interesa, dímelo y te guardo plaza.`;
};

// día y mes de un cumpleaños "YYYY-MM-DD" o "MM-DD"
export const birthdayMonthDay = (b) => {
  if (!b) return null;
  const parts = b.split("-");
  if (parts.length === 3) return { month: Number(parts[1]), day: Number(parts[2]) };
  if (parts.length === 2) return { month: Number(parts[0]), day: Number(parts[1]) };
  return null;
};

export const daysUntilBirthday = (b, ref = new Date()) => {
  const md = birthdayMonthDay(b);
  if (!md) return null;
  const today = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  let next = new Date(ref.getFullYear(), md.month - 1, md.day);
  if (next < today) next = new Date(ref.getFullYear() + 1, md.month - 1, md.day);
  return Math.round((next - today) / 86400000);
};

export const formatBirthday = (b) => {
  const md = birthdayMonthDay(b);
  if (!md) return "";
  return `${md.day} de ${MESES[md.month - 1]}`;
};

// Une fichas guardadas con clientes derivados de las citas (por teléfono)
export const mergeClients = (managed = [], appointments = []) => {
  const map = new Map();
  managed.forEach((c) => {
    const key = c.phone || c.name;
    map.set(key, { ...c, count: 0, total: 0, pending: 0, managed: true });
  });
  appointments.forEach((a) => {
    const key = a.client_phone || a.client_name;
    if (!map.has(key)) {
      map.set(key, {
        name: a.client_name,
        phone: a.client_phone || "",
        email: "",
        address: "",
        birthday: null,
        count: 0,
        total: 0,
        pending: 0,
        managed: false,
      });
    }
    const c = map.get(key);
    c.count += 1;
    c.total += a.price || 0;
    if (a.payment_status === "pendiente") c.pending += a.price || 0;
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
};

// Indicador de riesgo de olvido (señales mínimas)
export const computeRisk = (appt, all = []) => {
  const now = Date.now();
  const reasons = [];
  const missedBefore = all.some(
    (a) => a.client_phone === appt.client_phone && a.no_show && new Date(a.appointment_datetime).getTime() < now
  );
  if (missedBefore) reasons.push("Ha faltado antes");
  if (appt.payment_status === "pendiente") reasons.push("Pago pendiente");
  if (!appt.reminder_confirmed) reasons.push("Sin confirmar el recordatorio");

  let level = "bajo";
  if (missedBefore) level = "alto";
  else if (reasons.length > 0) level = "medio";
  return { level, reasons };
};
